import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material"
import { useState } from "react"
import { useDispatch } from "react-redux"
import { setMaevaSelectedDateRange } from "../../../redux/slices/metricSlice"
import { useSelector } from "react-redux"
import { format } from "date-fns"
import { DateRangePicker } from "react-date-range"
import "react-date-range/dist/styles.css"
import "react-date-range/dist/theme/default.css"
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import { ButtonWrapper, CustomDateRange, StyledButton } from "./Atom"
import {
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  subDays,
  subMonths,
  subYears,
} from "date-fns"

const predefinedRanges = [
  {
    label: "Today",
    range: () => ({
      startDate: new Date(),
      endDate: new Date(),
    }),
  },
  {
    label: "Yesterday",
    range: () => {
      const yesterday = subDays(new Date(), 1)
      return {
        startDate: yesterday,
        endDate: yesterday,
      }
    },
  },
  {
    label: "Last 7 Days",
    range: () => ({
      startDate: subDays(new Date(), 6),
      endDate: new Date(),
    }),
  },
  {
    label: "Last 30 Days",
    range: () => ({
      startDate: subDays(new Date(), 29),
      endDate: new Date(),
    }),
  },
  {
    label: "This Month",
    range: () => ({
      startDate: startOfMonth(new Date()),
      endDate: new Date(),
    }),
  },
  {
    label: "Previous Month",
    range: () => {
      const lastMonth = subMonths(new Date(), 1)
      return {
        startDate: startOfMonth(lastMonth),
        endDate: endOfMonth(lastMonth),
      }
    },
  },
  {
    label: "Year to Date",
    range: () => ({
      startDate: startOfYear(new Date()),
      endDate: new Date(),
    }),
  },
  {
    label: "Previous Year",
    range: () => {
      const previousYear = subYears(new Date(), 1)
      return {
        startDate: startOfYear(previousYear),
        endDate: endOfYear(previousYear),
      }
    },
  },
]

export default function DateRange2() {
  const dispatch = useDispatch()

  const [showDatePicker, setShowDatePicker] = useState<boolean>(false)
  const defaultStartDate = new Date()
  defaultStartDate.setDate(1)

  const defaultDateRange = {
    startDate: defaultStartDate,
    endDate: new Date(),
  }

  const startDateStore = useSelector((state: any) => {
    return state.metric.maevaSelectedDateRange?.startDate
  })

  const endDateStore = useSelector((state: any) => {
    return state.metric.maevaSelectedDateRange?.endDate
  })

  const [startDate, setStartDate] = useState<Date>(
    startDateStore ? new Date(startDateStore) : defaultDateRange.startDate,
  )
  const [endDate, setEndDate] = useState<Date>(
    endDateStore ? new Date(endDateStore) : defaultDateRange.endDate,
  )

  const handlePredefinedRangeClick = (
    rangeFn: () => { startDate: Date; endDate: Date },
  ) => {
    const { startDate, endDate } = rangeFn()
    setStartDate(startDate)
    setEndDate(endDate)
  }

  const updateStore = async () => {
    await dispatch(
      setMaevaSelectedDateRange({
        startDate: format(startDate, "MM-dd-yyyy"),
        endDate: format(endDate, "MM-dd-yyyy"),
      }),
    )
    setShowDatePicker(false)
  }

  // useEffect(() => {
  //   endDate && startDate && updateStore()
  // }, [endDate, startDate])

  const handleDateChange = (ranges: any) => {
    const { selection } = ranges
    setStartDate(selection.startDate)
    setEndDate(selection.endDate)
  }

  const renderDateRange = () => {
    if (startDate && endDate) {
      return `${format(startDate, "MM/dd/yyyy")} - ${format(
        endDate,
        "MM/dd/yyyy",
      )}`
    } else if (startDate) {
      return `${format(startDate, "MM/dd/yyyy")} - `
    } else {
      return "Select Date Range"
    }
  }

  return (
    <Box
      sx={{
        padding: "10px 0 10px 0",
        marginLeft: "2%",
        height: "40px",
      }}
    >
      <Typography fontSize={10}>Date Range</Typography>
      <TextField
        value={renderDateRange()}
        onClick={() => {
          setShowDatePicker(true)
        }}
        sx={{
          mt: "5px",
          minWidth: "240px",
          maxWidth: "300px",
          width: "fit-content",
          "& .MuiInputBase-root": {
            height: "40px",
            backgroundColor: "white",
          },
        }}
        aria-readonly
        InputProps={{
          endAdornment: (
            <InputAdornment position="end" className="marginRight-0">
              <IconButton edge="end">
                <CalendarTodayIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {/* <DateRangePicker
        ranges={[
          {
            startDate: startDate,
            endDate: endDate,
            key: "selection",
          },
        ]}
        onChange={handleDateChange}
        // locale={"en-GB"}
        // showMonthAndYearPickers={false}
        editableDateInputs={true} // Allow editing date inputs directly
        moveRangeOnFirstSelection={false} // Keep the picker open after selecting start date
        direction="horizontal"
        months={2}
        scroll={{
          calendarHeight: 400,
          monthHeight: 300,
          calendarWidth: 200,
          enabled: true,
        }}
      /> */}
      <Box
        sx={{
          display: showDatePicker ? "flex" : "none",
          backgroundColor: "white",
          position: "absolute",
          zIndex: 10,
          margin: "-60px 0 0 -20px",
          boxShadow: 2,
          borderRadius: "4px",
        }}
      >
        {/* Left Quick Panel */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            padding: "16px 12px",
            borderRight: "1px solid #ddd",
            backgroundColor: "#fafafa",
            minWidth: "160px",
          }}
        >
          {predefinedRanges.map((item) => (
            <StyledButton
              key={item.label}
              variant="text"
              onClick={() => handlePredefinedRangeClick(item.range)}
              sx={{
                justifyContent: "flex-start",
                textAlign: "left",
                padding: "8px",
                fontSize: "14px",
                color: "#333",
              }}
            >
              {item.label}
            </StyledButton>
          ))}
        </Box>

        {/* Calendar Picker */}
        <Box>
          <CustomDateRange
            ranges={[
              {
                startDate: startDate,
                endDate: endDate,
                key: "selection",
              },
            ]}
            onChange={handleDateChange}
            editableDateInputs={true}
            moveRangeOnFirstSelection={false}
            direction="horizontal"
            months={2}
            maxDate={new Date()}
            scroll={{
              calendarHeight: 100,
              monthHeight: 290,
            }}
          />
          <ButtonWrapper>
            <StyledButton
              onClick={() => {
                setStartDate(
                  startDateStore
                    ? new Date(startDateStore)
                    : defaultDateRange.startDate,
                )
                setEndDate(
                  endDateStore
                    ? new Date(endDateStore)
                    : defaultDateRange.endDate,
                )
                setShowDatePicker(false)
              }}
              variant="outlined"
            >
              Cancel
            </StyledButton>
            <StyledButton
              onClick={updateStore}
              variant="contained"
              color="primary"
            >
              Apply
            </StyledButton>
          </ButtonWrapper>
        </Box>
      </Box>
    </Box>
  )
}
